import { Injectable } from '@angular/core';
import { BLOCK_ELEMENTS } from './constants';

export type EditorCommandType =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'insertUnorderedList'
  | 'insertOrderedList'
  | 'justifyLeft'
  | 'justifyCenter'
  | 'justifyRight'
  | 'justifyFull'
  | 'formatBlock'
  | 'foreColor'
  | 'backColor'
  | 'createLink'
  | 'unlink'
  | 'removeFormat'
  | 'undo'
  | 'redo'
  | 'indent'
  | 'outdent';

export interface IFormatState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  unorderedList: boolean;
  orderedList: boolean;
  justifyLeft: boolean;
  justifyCenter: boolean;
  justifyRight: boolean;
  justifyFull: boolean;
  currentFormat: string;
  currentForeColor: string;
  currentBackColor: string;
}

@Injectable({
  providedIn: 'root'
})
export class RichTextEditorCommandService {
  justifyLeftApplied = false;

  executeCommand(command: EditorCommandType, value: string | null = null): void {
    document.execCommand(command, false, value);
  }

  isCommandActive(command: string): boolean {
    if (this.isAlignmentCommand(command)) {
      const element = this.getCurrentElement();
      const blockElement = element?.closest(BLOCK_ELEMENTS) as HTMLElement;

      if (!blockElement || !blockElement.style.textAlign) {
        return false;
      }
    }

    return document.queryCommandState(command) || false;
  }

  private isAlignmentCommand(command: string): boolean {
    return ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'].includes(command);
  }

  getCommandValue(command: EditorCommandType): string {
    return document.queryCommandValue(command);
  }

  getCurrentElement(): HTMLElement | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return null;
    }

    let element = selection.getRangeAt(0).startContainer as HTMLElement;

    if (element.nodeType === Node.TEXT_NODE) {
      element = element.parentElement;
    }

    return element;
  }

  getCurrentFormat(editorElement: HTMLElement): string {
    const element = this.getCurrentElement();
    if (!element) {
      return '';
    }

    const formatElement = element.closest('p, blockquote, pre, h1, h2, h3, h4, h5, h6');

    if (formatElement && editorElement.contains(formatElement)) {
      return formatElement.tagName.toLowerCase();
    }

    return '';
  }


  getFormatState(editorElement: HTMLElement): IFormatState {
    return {
      bold: this.isCommandActive('bold'),
      italic: this.isCommandActive('italic'),
      underline: this.isCommandActive('underline'),
      strikethrough: this.isCommandActive('strikethrough'),
      unorderedList: this.isCommandActive('insertUnorderedList'),
      orderedList: this.isCommandActive('insertOrderedList'),
      justifyLeft: this.isCommandActive('justifyLeft'),
      justifyCenter: this.isCommandActive('justifyCenter'),
      justifyRight: this.isCommandActive('justifyRight'),
      justifyFull: this.isCommandActive('justifyFull'),
      currentFormat: this.getCurrentFormat(editorElement),
      currentForeColor: this.getCommandValue('foreColor'),
      currentBackColor: this.getCommandValue('backColor')
    };
  }

  hasSelection(): boolean {
    const selection = window.getSelection();
    return !!(selection && selection.rangeCount > 0 && !selection.isCollapsed);
  }

  getSelectedText(): string {
    const selection = window.getSelection();
    return selection?.toString() || '';
  }

  saveSelection(): Range | null {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  }

  restoreSelection(range: Range): void {
    if (range) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  isWithinEditor(element: HTMLElement | null, editorElement: HTMLElement): boolean {
    if (!element) {
      return false;
    }
    return editorElement.contains(element);
  }

  reset(): void {
    this.justifyLeftApplied = false;
  }


  insertHTML(html: string): void {
    document.execCommand('insertHTML', false, html);
  }

  clearFormatting(): void {
    return this.executeCommand('removeFormat');
  }
}
